/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, Fragment } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { TextField, Label, Stack } from '@bingads-webui-react/fabric-control';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import appConfig from '@bingads-webui-component-legacy/config/index';
import { getBidErrorMsg, validateNumber, getAccountCurrencyCode, globalErrorMsg, bidStrategyOptionKeys, Disclaimer } from '@bingads-webui-campaign/hotel-common-upsert';
import { ErrorString, checkShouldShowError, } from '@bingads-webui-campaign-react/wizard-shared-components';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';

export const bidFields = {
  commissionRate: 'commissionRate',
  percentCpcBid: 'percentCpcBid',
  searchBid: 'searchBid',
};

export const getBaseBidMinMax = ({ bidStrategyType, serverContext }) => {
  const min = (serverContext && serverContext.KeywordBidRange && serverContext.KeywordBidRange.FloorValue) || 0.01;
  const max = (serverContext && serverContext.KeywordBidRange && serverContext.KeywordBidRange.CeilingValue) || 1000;
  // align the range with search ad group campaign as MT create it with the same flow.
  switch (bidStrategyType) {
    case bidStrategyOptionKeys.Commission:
      return { min: appConfig.get('KeywordCommissionRateRange') && appConfig.get('KeywordCommissionRateRange').FloorValue, max: appConfig.get('KeywordCommissionRateRange') && appConfig.get('KeywordCommissionRateRange').CeilingValue };
    case bidStrategyOptionKeys.PercentCpc:
      return { min: appConfig.get('KeywordPercentCpcBidRange') && appConfig.get('KeywordPercentCpcBidRange').FloorValue, max: appConfig.get('KeywordPercentCpcBidRange') && appConfig.get('KeywordPercentCpcBidRange').CeilingValue };
    case bidStrategyOptionKeys.ManualCpc:
    default:
      return { min, max };
  }
};

export const validateBaseBid = ({
  baseBid, bidStrategyType, min, max,
}) => {
  switch (bidStrategyType) {
    case bidStrategyOptionKeys.Commission:
      return validateNumber({
        value: baseBid.commissionRate, min, max, notRequired: true, digitLength: 2, notIncludeMin: true,
      });
    case bidStrategyOptionKeys.PercentCpc:
      return validateNumber({
        value: baseBid.percentCpcBid, min, max, digitLength: 2, notIncludeMin: true,
      });
    case bidStrategyOptionKeys.ManualCpc:
    default:
      return validateNumber({
        value: baseBid.searchBid, min, max, digitLength: 2,
      });
  }
};

export const isBaseBidValid = ({ baseBid, bidStrategyType, serverContext }) => validateBaseBid({ baseBid, bidStrategyType, ...getBaseBidMinMax({ bidStrategyType, serverContext }) }) == null;
export const getBaseBidErrorMessage = ({ baseBid, bidStrategy, serverContext }) => {
  const bidStrategyType = bidStrategy && bidStrategy.option;
  if (!isBaseBidValid({ baseBid, bidStrategyType, serverContext }) && checkShouldShowError(baseBid)) {
    return globalErrorMsg;
  }
  return '';
};

export const getBaseBidTitleDetails = (i18n, baseBid, bidStrategy, accountCurrencyCode) => {
  const bidStrategyType = bidStrategy && bidStrategy.option;
  switch (bidStrategyType) {
    case bidStrategyOptionKeys.Commission:
      return `${i18n.getString(_TL_('Commission rate: '))}${baseBid.commissionRate || bidStrategy.Commission || ''}%`;
    case bidStrategyOptionKeys.PercentCpc:
      return `${i18n.getString(_TL_('Percent bid: '))}${baseBid.percentCpcBid || ''}%`;
    case bidStrategyOptionKeys.ManualCpc:
    default:
      return `${i18n.getString(_TL_('Bid: '))}${baseBid.searchBid || ''} ${accountCurrencyCode}`;
  }
};

const getStyles = () => ({
  baseBid: {
    '& .subtitle': {
      marginBottom: '28px',
      fontSize: '20px',
    },
    '& .dashline': {
      borderBottom: '1px solid',
      width: '100%',
      margin: '24px 0',
    },
    '& .alert': {
      marginTop: '8px',
    },
  },
});

const BidOrPercent = (props) => {
  const {
    title,
    field,
    baseBid,
    setBaseBid,
    childPart,
  } = props;
  return (
    <Fragment>
      <Label>{title}</Label>
      <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
        <TextField
          ariaLabel={`${field}Label`}
          value={baseBid[field]}
          onChange={(e, value) => {
            setBaseBid({
              [field]: value,
              isDirty: true,
            });
          }}
          styles={{ fieldGroup: { width: 110 } }}
          className={field}
        />
        {childPart()}
      </Stack>
    </Fragment>
  );
};

BidOrPercent.propTypes = {
  baseBid: PropTypes.shape({}),
  setBaseBid: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  field: PropTypes.string.isRequired,
  childPart: PropTypes.func.isRequired,
};

BidOrPercent.defaultProps = {
  baseBid: {
    value: undefined,
    isDirty: false,
  },
};

const CampaignBidInput = (props) => {
  const i18n = useContext(I18nContext);
  const {
    bidStrategy,
  } = props;
  const bidStrategyType = bidStrategy && bidStrategy.option;
  switch (bidStrategyType) {
    case bidStrategyOptionKeys.Commission:
      return (
        <div className="campaign-rate-bid">
          <Label>{i18n.getString(_TL_('Campaign commission rate'))}</Label>
          <div className="campaign-rate-bid-value">
            <span>{`${bidStrategy.Commission}%`}</span>
          </div>
        </div>
      );
    default:
      return null;
  }
};


CampaignBidInput.propTypes = {
  bidStrategy: PropTypes.shape({}).isRequired,
};

CampaignBidInput.defaultProps = {
};


const BidInput = (props) => {
  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);
  const accountCurrencyCode = getAccountCurrencyCode(serverContext);
  const {
    baseBid,
    setBaseBid,
    bidStrategyType,
  } = props;
  switch (bidStrategyType) {
    case bidStrategyOptionKeys.Commission:
      return (<BidOrPercent
        title={i18n.getString(_TL_('Commission rate'))}
        field={bidFields.commissionRate}
        baseBid={baseBid}
        setBaseBid={setBaseBid}
        childPart={() =>
          (<span>%</span>)
        }
      />);
    case bidStrategyOptionKeys.PercentCpc:
      return (<BidOrPercent
        title={i18n.getString(_TL_('Percent bid'))}
        field={bidFields.percentCpcBid}
        baseBid={baseBid}
        setBaseBid={setBaseBid}
        childPart={() =>
          (<span>%</span>)
        }
      />);
    case bidStrategyOptionKeys.ManualCpc:
    default:
      return (<BidOrPercent
        title={i18n.getString(_TL_('Bid'))}
        field={bidFields.searchBid}
        baseBid={baseBid}
        setBaseBid={setBaseBid}
        childPart={() =>
          (<span>{i18n.getString(_TL_('{{item}}'), { item: accountCurrencyCode })}</span>)
        }
      />);
  }
};

BidInput.propTypes = {
  baseBid: PropTypes.shape({}),
  setBaseBid: PropTypes.func.isRequired,
  bidStrategyType: PropTypes.string.isRequired,
};

BidInput.defaultProps = {
  baseBid: {
    value: undefined,
    isDirty: false,
  },
};


export const BaseBid = React.memo((props) => {
  const {
    baseBid,
    setBaseBid,
    bidStrategy,
  } = props;
  const bidStrategyType = bidStrategy && bidStrategy.option;
  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);
  const minMax = getBaseBidMinMax({ bidStrategyType, serverContext });
  const error = getBidErrorMsg({
    code: validateBaseBid({ baseBid, bidStrategyType, ...minMax }),
    i18n,
    ...minMax,
    isPercent: bidStrategyType === bidStrategyOptionKeys.PercentCpc || bidStrategyType === bidStrategyOptionKeys.Commission,
  });
  const shouldShowError = checkShouldShowError(baseBid);
  const classes = useDefaultStyles(getStyles);

  return (
    <div className={classnames('base-bid', classes.baseBid)}>
      <CampaignBidInput bidStrategy={bidStrategy} />
      <div className="bid-input">
        <BidInput baseBid={baseBid} setBaseBid={setBaseBid} bidStrategyType={bidStrategyType} />
      </div>
      {shouldShowError && error && <ErrorString error={error} />}
      {(bidStrategyType === bidStrategyOptionKeys.Commission) && <Disclaimer
        message={i18n.getString(_TL_('If the ad group level commission bid rate is not specified, the campaign level commission bid rate will be applied.'))}
      />}
    </div>
  );
});

BaseBid.propTypes = {
  baseBid: PropTypes.shape({}),
  setBaseBid: PropTypes.func.isRequired,
  bidStrategy: PropTypes.shape({}).isRequired,
};

BaseBid.defaultProps = {
  baseBid: {
    searchBid: undefined,
    percentCpcBid: undefined,
    commissionRate: undefined,
    isDirty: false,
  },
};
